/**
 * The pure parts of /audio-converter and /audio-joiner's page script (`audio-convert.ts`): which
 * dropped files start a job, where a dragged row lands, and what the page names. Pure, so the
 * tests read it directly.
 */

import { classify } from '../../../base/src/lib/dropzone.ts';
import type { ConvertTarget } from './convert-protocol.ts';

/** A dropped file either starts the job or gets the "can't be read" banner. */
export type ConvertDrop = 'start' | 'unreadable';

/**
 * Audio files and the videos the engine opens start the job. A WMV, FLV or MPG (which nothing here
 * opens), a subtitle file, an archive or anything else is refused before any work.
 */
export function routeConvertDrop(file: File): ConvertDrop {
  const kind = classify(file);
  return kind === 'audio' || kind === 'container' ? 'start' : 'unreadable';
}

/**
 * Where a dragged row lands: the number of the OTHER rows whose middle is above the pointer.
 * `middles` are those rows' vertical middles, top to bottom, without the dragged one.
 */
export function dropIndex(middles: readonly number[], y: number): number {
  let index = 0;
  for (const middle of middles) if (middle < y) index += 1;
  return index;
}

/** The file type a target saves, as the Download button names it. */
export function targetType(target: ConvertTarget): 'MP3' | 'WAV' {
  return target.kind === 'wav' ? 'WAV' : 'MP3';
}

/** What Save hands back, as the result row says it: `MP3 · 320 kbps`, or `WAV`. */
export function targetName(target: ConvertTarget): string {
  return target.kind === 'wav' ? 'WAV' : `MP3 · ${target.kbps} kbps`;
}
